/**
 * Every trade a business works in — the list, not the label.
 *
 * ## Why there are two columns
 *
 * `accounts.trade` is the MAIN trade, picked once on the last step of signup.
 * It is load-bearing in a dozen places that have nothing to do with this
 * feature: the admin account filter, the mobile home's "try saying"
 * suggestions, suggested qualifications, onboarding card `trade_filter`, the
 * HubSpot property. Widening it into an array would have meant rewriting every
 * one of those readers to answer a question none of them is asking, and any
 * one of them missed is a wrong answer nobody would see.
 *
 * So `accounts.trades` is a second column carrying the FULL list, main trade
 * first, and `accounts.trade` continues to mean exactly what it always meant.
 *
 * 🚨 The list INCLUDES the main trade — it is not "the extras". A shape where
 * the caller has to remember to prepend `trade` is a shape where somebody
 * forgets, and a forgotten main trade reads as a business that does not do the
 * thing it signed up to do. `accountTrades()` is the only way to read it, and
 * it repairs the row on the way out: an account written before the column
 * existed, or one whose backfill has not run yet, still answers `[trade]`.
 */
import { TRADE_OPTIONS } from '@/lib/trade-presets';

/** The two columns as they come off `accounts`. Both may be absent or null. */
export interface AccountTradeRow {
  trade?: string | null;
  trades?: string[] | null;
}

/** A trade value trimmed to the form we store and compare. */
const clean = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * Every trade this account works in, main trade first, deduped, never empty of
 * a main trade that exists.
 *
 * Order is meaning here: the first entry is the business's own answer to "what
 * are you?", and it is what a prompt reads out first.
 */
export function accountTrades(row: AccountTradeRow | null | undefined): string[] {
  const main = clean(row?.trade);
  const rest = Array.isArray(row?.trades) ? row!.trades!.map(clean).filter(Boolean) : [];
  const out: string[] = [];
  for (const t of [main, ...rest]) {
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
}

/**
 * The label an owner would recognise. A trade we know is printed as its
 * catalogue label; anything else is a custom trade the owner typed themselves,
 * and is printed verbatim rather than dropped — the whole point of the free
 * text box is that they get to name their own work.
 */
export function tradeLabel(value: string): string {
  const known = TRADE_OPTIONS.find((t) => t.value === value);
  return known ? known.label : value;
}

/** The labels, in the order `accountTrades` returns them. */
export function tradeLabels(row: AccountTradeRow | null | undefined): string[] {
  return accountTrades(row).map(tradeLabel);
}

/**
 * One line naming the trades, for a prompt. Null when we do not know any —
 * a prompt line that says "not set" teaches the model to talk about our
 * database rather than the caller's problem.
 */
export function describeTrades(row: AccountTradeRow | null | undefined): string | null {
  const labels = tradeLabels(row);
  if (labels.length === 0) return null;
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * Normalise a list posted by the settings picker: trimmed, deduped, main trade
 * forced to the front, capped.
 *
 * The cap is not a guess at how many trades a business can have — it is a cap
 * on how much of a prompt this can spend and how many preset families one
 * request can seed. Twelve is more than double the largest real list we have
 * seen and still one short line.
 */
export const MAX_TRADES = 12;

export function normaliseTradeList(main: string | null | undefined, posted: unknown): string[] {
  const list = Array.isArray(posted) ? posted.map(clean).filter(Boolean) : [];
  const mainClean = clean(main);
  const out: string[] = [];
  for (const t of [mainClean, ...list]) {
    if (t && !out.includes(t)) out.push(t);
  }
  return out.slice(0, MAX_TRADES);
}
